/**
 * Bayan's 10-digit number rule, for the two fields that carry it.
 *
 * A Saudi national id and an iqama are both exactly 10 digits, and so is
 * a commercial registration / unified establishment number. ELM enforces
 * that with a validation message that names no field at all — a real
 * refusal read `[ length must be 10]`, which is why Hero has to know the
 * rule itself rather than relaying the provider's version of it.
 *
 * Observed 19 Aug 2026: a driver was saved with a 9-digit id (`120282827`)
 * because nothing between the chat and the government API checked the
 * length. `create_trip` refused, the refusal named nothing, and a
 * `rejected` error is never retried — so the وثيقة simply never arrived
 * and the person was told `[ length must be 10]`.
 *
 * Establishment numbers (الرقم الموحد / هوية صاحب العمل) are a stricter
 * subset: 10 digits whose first digit is `7`. Old CRs starting with `1`
 * are not accepted as `carrier.moi`. National ids keep the looser
 * 10-digit-any-prefix rule — they typically start with 1 or 2.
 */
const BAYAN_IDENTITY_PATTERN = /^\d{10}$/;
const BAYAN_ESTABLISHMENT_PATTERN = /^7\d{9}$/;

/** How many digits Bayan wants on an identity / CR number. */
export const BAYAN_IDENTITY_DIGITS = 10;

/**
 * Arabic-Indic (٠-٩) and Extended Arabic-Indic (۰-۹) digits, in order.
 *
 * People type their id in the numerals of the keyboard they are writing
 * with — the same threads that produce `ارص ١٢٣٤` produce ids in Arabic
 * numerals — and a government payload takes Western digits only.
 */
const EASTERN_DIGITS = '٠١٢٣٤٥٦٧٨٩۰۱۲۳۴۵۶۷۸۹';

const toWesternDigits = (value: string): string =>
  value.replace(/[٠-٩۰-۹]/g, digit => String(EASTERN_DIGITS.indexOf(digit) % 10));

const toNormalizedDigits = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  return toWesternDigits(value).replace(/[\s\-_./\\]+/g, '');
};

/**
 * A national id / iqama in the form Bayan accepts, or `undefined` when
 * the value cannot be one.
 *
 * Normalizes the numerals and drops the separators people write inside a
 * long number (`1202-828-279`, `١٢٠٢ ٨٢٨ ٢٧٩`), then requires exactly
 * {@link BAYAN_IDENTITY_DIGITS} digits. Never pads or truncates: a
 * 9-digit id is one the user has to re-read, and inventing the tenth
 * digit would file a document against somebody else's identity.
 *
 * Do not use this for a CR / MOI — those must start with `7`. See
 * {@link toBayanEstablishmentNumber}.
 */
export const toBayanIdentityNumber = (value: string | undefined): string | undefined => {
  const digits = toNormalizedDigits(value);
  return digits && BAYAN_IDENTITY_PATTERN.test(digits) ? digits : undefined;
};

/** Whether a value already satisfies Bayan's identity contract. */
export const isBayanIdentityNumber = (value: string | undefined): boolean =>
  toBayanIdentityNumber(value) !== undefined;

/**
 * A Saudi unified establishment number (الرقم الموحد) in the form Bayan
 * accepts as `carrier.moi`, or `undefined` when the value cannot be one.
 *
 * Same numeral / separator normalization as {@link toBayanIdentityNumber},
 * then requires exactly 10 digits whose first digit is `7`. `70…` and
 * `71…` both pass; a legacy CR starting with `1` does not.
 */
export const toBayanEstablishmentNumber = (value: string | undefined): string | undefined => {
  const digits = toNormalizedDigits(value);
  return digits && BAYAN_ESTABLISHMENT_PATTERN.test(digits) ? digits : undefined;
};

/** Whether a value already satisfies Bayan's establishment-number contract. */
export const isBayanEstablishmentNumber = (value: string | undefined): boolean =>
  toBayanEstablishmentNumber(value) !== undefined;

/**
 * Luhn over the 10 digits. Saudi national / iqama numbers carry it, and
 * so does every 70… Bayan has accepted (21 of 21, Oct 2026; 7040636982
 * passes, its ٦→٩ misread 7040639982 fails) — one printed operator
 * number (7001007244) fails and is unverified. So a failing read is
 * re-read and ranked below a passing one; it is never refused.
 */
export const passesIdentityChecksum = (value: string | undefined): boolean => {
  const digits = toNormalizedDigits(value);
  if (!digits || !/^\d{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[i]);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
};

/** Digit runs long enough to be an id / MOI (Western + Eastern-Arabic). */
const DIGIT_RUN_RE = /[0-9٠-٩۰-۹]{6,16}/g;

export const digitRunsFromText = (text: string): string[] => text.match(DIGIT_RUN_RE) ?? [];

/**
 * Map a typed MOI onto a card candidate when they are clearly the same
 * number (dropped digit, extra digit, transpose, stopped mid-number).
 *
 * Inventing a tenth digit with *no* candidate is still forbidden — that
 * would file against the wrong establishment. A unique close match to a
 * number already on the إقامة / استمارة is not invention: they were
 * copying the card (runtime: `703147836` for `7031478436`).
 *
 * A *valid* 10-digit 7… that is far from every candidate is a different
 * company — keep what they typed.
 */
export const resolveTypedEstablishmentNumber = (
  typed: string | undefined,
  candidates: Array<string | undefined> = [],
): string | undefined => {
  const pool = [
    ...new Set(
      candidates
        .map(candidate => toBayanEstablishmentNumber(candidate))
        .filter((candidate): candidate is string => Boolean(candidate)),
    ),
  ];
  const exact = toBayanEstablishmentNumber(typed);
  if (exact) {
    if (pool.includes(exact)) return exact;
    // One edit from a card read is a typo only when the checksum says so.
    // Otherwise it is the correction of a misread card (thread 6abbaddd:
    // typed 7040636982, card read 7040639982) and must not snap back.
    const close = uniqueClosest(exact, pool, 1);
    return close && !passesIdentityChecksum(exact) && passesIdentityChecksum(close)
      ? close
      : exact;
  }
  const digits = toNormalizedDigits(typed);
  if (!digits || digits.length < 6 || digits.length > 11) return undefined;
  const prefixHits = pool.filter(candidate => candidate.startsWith(digits) && digits.length >= 7);
  if (prefixHits.length === 1) return prefixHits[0];
  return uniqueClosest(digits, pool, 2);
};

const uniqueClosest = (typed: string, pool: string[], maxDistance: number): string | undefined => {
  let best: string | undefined;
  let bestDistance = maxDistance + 1;
  let ties = 0;
  for (const candidate of pool) {
    const distance = digitLevenshtein(typed, candidate);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
      ties = 1;
    } else if (distance === bestDistance) {
      ties += 1;
    }
  }
  return best && bestDistance <= maxDistance && ties === 1 ? best : undefined;
};

const digitLevenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i += 1) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= n; j += 1) {
      const nextDiag = prev[j];
      prev[j] = a[i - 1] === b[j - 1] ? diag : 1 + Math.min(diag, prev[j], prev[j - 1]);
      diag = nextDiag;
    }
  }
  return prev[n];
};
